<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-bold text-gray-800">Team Members</h2>
      <button
        class="bg-brand-black p-3 rounded-xl text-white cursor-pointer disabled:bg-opacity-10 disabled:cursor-not-allowed"
        @click="handleSave"
        :disabled="!canSave || teams.length === 0"
      >
        Save Changes
      </button>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card v-for="(member, index) in teams" :key="index" class="relative group" v-if="teams.length >= 1">
        <CardHeader class="flex flex-col items-center pb-2">
          <!-- <Avatar v-if="newMember.image_url" :alt="member.name" class="h-24 w-24 mb-2">{{ getInitials(member.name) }}</Avatar> -->
          <Avatar v-if="imagePreviews.image_url && editingIndex === index" :src="imagePreviews.image_url" :alt="member.name" class="h-24 w-24 mb-2" />
          <Avatar v-else :src="member.image_url" :alt="member.name" class="h-24 w-24 mb-2">
            {{ getInitials(member.name) }}
          </Avatar>
          <CardTitle class="text-center">{{ member.name }}</CardTitle>
        </CardHeader>
        <CardContent class="text-center">
          <p class="text-gray-600">{{ member.position }}</p>
        </CardContent>
        <CardFooter class="justify-center">
          <Button
            variant="secondary"
            size="sm"
            class="opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 left-2"
            @click="handleEditMember(index)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            class="opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-2"
            @click="handleRemoveMember(index)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </Button>
        </CardFooter>
      </Card>
      <div v-else class="col-span-3 text-center font-semibold text-xl border py-5 rounded-md">
        <p>Add team members</p>
      </div>
    </div>

    <Card class="mt-6">
      <CardHeader>
        <CardTitle>Add New Team Member</CardTitle>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label for="name">Full Name</Label>
            <Input id="name" v-model="newMember.name" placeholder="e.g., John Doe" />
          </div>

          <div>
            <Label for="position">Position</Label>
            <Input id="position" v-model="newMember.position" placeholder="e.g., Marketing Manager" />
          </div>

          <div>
            <Label for="photoUrl">Photo URL</Label>
            <form id="imageForm" class="flex">
              <Input id="photoUrl" disabled v-model="newMember.image_url" placeholder="Upload profile photo" class="rounded-r-none" />
              <Button class="rounded-l-none" variant="secondary" @click.prevent="handleFileClick">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
              </Button>
              <input ref="fileInput" type="file" name="file" accept="image/*" class="hidden" @change="handleFileChange($event, 'image_url')" />
            </form>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <div class="flex items-center gap-2">
          <button v-if="editingIndex !== null" class="bg-brand-black p-3 rounded-xl text-white cursor-pointer flex items-center" @click="cancelEdit">
            Cancel
          </button>
          <button
            class="bg-brand-black p-3 rounded-xl text-white cursor-pointer flex items-center disabled:bg-opacity-10 disabled:cursor-not-allowed"
            @click="editingIndex !== null ? handleUpdateMember() : handleAddMember()"
            :disabled="isDisabled"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 mr-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            {{ editingIndex !== null ? 'Update Team Member' : 'Add Team Member' }}
          </button>
        </div>
        <small class="text-sm font-semibold mt-4 text-red-500">* Save changes before you continue</small>
      </CardFooter>
    </Card>
    <Loading :isLoading="isLoading" className="fixed inset-0 bg-[#1B224B]/[.2] flex justify-center items-center" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import Button from '../ui/Button.vue';
import Input from '../ui/Input.vue';
import Label from '../ui/Label.vue';
import Card from '../ui/Card.vue';
import CardHeader from '../ui/CardHeader.vue';
import CardTitle from '../ui/CardTitle.vue';
import CardContent from '../ui/CardContent.vue';
import CardFooter from '../ui/CardFooter.vue';
import Avatar from '../ui/Avatar.vue';
import Loading from '@/components/Loading.vue';
import { useStore } from 'vuex';

const store = useStore();
const props = defineProps({ companyDetails: { type: Object } });
const emit = defineEmits(['refreshCompanyProfile']);

const teams = ref([]);
const newTeams = ref([]); // Track only new team members
const newMember = reactive({
  name: '',
  position: '',
  image_url: '',
});
const imagePreviews = ref({
  image_url: '',
});
const fileInput = ref(null);
const canSave = ref(false);
const isLoading = ref(false);
const editingIndex = ref(null);

const handleFileClick = () => {
  fileInput.value.click();
};

const handleFileChange = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Show preview
  const reader = new FileReader();
  reader.onload = (e) => {
    imagePreviews.value.image_url = e.target.result;
  };
  reader.readAsDataURL(file);

  const formData = new FormData();
  formData.append('file', file);

  try {
    isLoading.value = true;

    const fileUrl = await store.dispatch('global/uploadFileUrl', formData);
    newMember.image_url = fileUrl;
  } catch (err) {
    console.error('Upload failed:', err.response?.data || err.message);
    alert('Upload failed: ' + (err.response?.data?.message || err.message));
  } finally {
    isLoading.value = false;
  }
};

const handleAddMember = () => {
  if (newMember.name && newMember.position) {
    const member = {
      ...newMember,
      image_url: newMember.image_url || '/placeholder.svg?height=100&width=100',
    };
    teams.value.push(member);
    newTeams.value.push(member); // Track only newly added members

    resetForm();
  }
};

const handleUpdateMember = () => {
  if (newMember.name && newMember.position) {
    const updatedMember = {
      ...newMember,
      image_url: newMember.image_url || '/placeholder.svg?height=100&width=100',
    };
    teams.value[editingIndex.value] = updatedMember;

    // Update newTeams if the edited member was newly added
    const newTeamIndex = newTeams.value.findIndex((m, idx) => idx === editingIndex.value && !props.companyDetails.teams.includes(m));
    if (newTeamIndex !== -1) {
      newTeams.value[newTeamIndex] = updatedMember;
    } else {
      newTeams.value.push(updatedMember); // Track edited member as a change
    }

    resetForm();
    canSave.value = true;
  }
};

const handleEditMember = (index) => {
  editingIndex.value = index;
  const member = teams.value[index];
  console.log(member);
  newMember.name = member.name;
  newMember.position = member.position;
  newMember.image_url = member.image_url;
  newMember.employer_id = member.employer_id;
  newMember.id = member.id;
};

const handleRemoveMember = (id) => {
  teams.value = teams.value.filter((_, index) => index !== id);
  // Optional: remove from `newTeams` too if needed
};

const getInitials = (name = '') => {
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

const isDisabled = computed(() => {
  return !(newMember.name && newMember.position);
});

const cancelEdit = () => {
  resetForm();
};

const resetForm = () => {
  newMember.name = '';
  newMember.position = '';
  newMember.image_url = '';
  imagePreviews.value.image_url = '';
  editingIndex.value = null;
};

const handleSave = async () => {
  if (newTeams.value.length === 0) return;

  isLoading.value = true;
  newTeams.value.forEach((team) => {
    team.id = String(team.id);
  });
  try {
    await store.dispatch('auth/updateUserProfile', { teams: newTeams.value });
    newTeams.value = [];
    canSave.value = false;
    emit('refreshCompanyProfile');
    imagePreviews.value.image_url = '';
  } catch (error) {
    console.error('Error saving team updates:', error);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => newTeams.value.length,
  (newLength) => {
    canSave.value = newLength > 0;
  },
  { flush: 'post' },
);

onMounted(() => {
  isLoading.value = true;
  if (props.companyDetails && Array.isArray(props.companyDetails.teams)) {
    teams.value = [...props.companyDetails.teams];
  }
  isLoading.value = false;
});
</script>
